import type { i18n as I18nInstance } from "i18next";

type DeepObject = Record<string, unknown>;

export function deepMerge(base: DeepObject, override: DeepObject): DeepObject {
  const result: DeepObject = { ...base };

  for (const key of Object.keys(override)) {
    const baseValue = base[key];
    const overrideValue = override[key];

    if (Array.isArray(overrideValue)) {
      result[key] = overrideValue;
    } else if (
      overrideValue !== null &&
      typeof overrideValue === "object" &&
      !Array.isArray(overrideValue) &&
      baseValue !== null &&
      typeof baseValue === "object" &&
      !Array.isArray(baseValue)
    ) {
      result[key] = deepMerge(
        baseValue as DeepObject,
        overrideValue as DeepObject,
      );
    } else {
      result[key] = overrideValue;
    }
  }

  return result;
}

type GlobModule = { default: DeepObject };

const baseLocales = import.meta.glob<GlobModule>("../locales/**/*.json", {
  eager: false,
});
const productLocales = import.meta.glob<GlobModule>(
  "../products/*/locales/**/*.json",
  { eager: false },
);
const clientLocales = import.meta.glob<GlobModule>(
  "../products/*/*/locales/**/*.json",
  { eager: false },
);

async function loadBaseTranslations(
  lng: string,
  ns: string,
): Promise<DeepObject | null> {
  const path = `../locales/${lng}/${ns}.json`;
  const loader = baseLocales[path];

  if (!loader) {
    return null;
  }

  try {
    const module = await loader();
    return module.default;
  } catch {
    return null;
  }
}

async function loadProductTranslations(
  productId: string,
  lng: string,
  ns: string,
): Promise<DeepObject | null> {
  const path = `../products/${productId}/locales/${lng}/${ns}.json`;
  const loader = productLocales[path];

  if (!loader) {
    return null;
  }

  try {
    const module = await loader();
    return module.default;
  } catch {
    return null;
  }
}

async function loadClientTranslations(
  productId: string,
  clientId: string,
  lng: string,
  ns: string,
): Promise<DeepObject | null> {
  const path = `../products/${productId}/${clientId}/locales/${lng}/${ns}.json`;
  const loader = clientLocales[path];

  if (!loader) {
    return null;
  }

  try {
    const module = await loader();
    return module.default;
  } catch {
    return null;
  }
}

export async function loadNamespace(
  i18n: I18nInstance,
  lng: string,
  ns: string,
  productId: string,
  clientId?: string,
): Promise<void> {
  const baseTranslations = await loadBaseTranslations(lng, ns);

  if (!baseTranslations) {
    console.warn(`[i18n] Base translations not found for ${lng}/${ns}`);
    return;
  }

  let merged = baseTranslations;

  const productTranslations = await loadProductTranslations(productId, lng, ns);
  if (productTranslations) {
    merged = deepMerge(merged, productTranslations);
  }

  if (clientId) {
    const clientTranslations = await loadClientTranslations(
      productId,
      clientId,
      lng,
      ns,
    );
    if (clientTranslations) {
      merged = deepMerge(merged, clientTranslations);
    }
  }

  i18n.addResourceBundle(lng, ns, merged, true, true);
}

export async function preloadI18n(
  i18n: I18nInstance,
  lng: string,
  namespaces: string[],
  productId: string,
  clientId?: string,
): Promise<void> {
  await Promise.all(
    namespaces.map((ns) => loadNamespace(i18n, lng, ns, productId, clientId)),
  );
}

export const DEFAULT_NAMESPACES = ["common", "auth", "errors"] as const;
